import React, { FC, useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Tabs, TabsList, TabsTrigger, cn } from '@column/ui';

import { useNavigate } from '~/lib/navigation';

export interface TabEntry {
  label?: React.ReactNode;
  path: string;
  hide?: boolean;
  activePath?: string;
  removeWildcard?: boolean;
}

interface PageTabsProps {
  entries?: TabEntry[];
  className?: string;
}

export const PageTabs: FC<PageTabsProps> = ({ className, entries }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const replaceParams = useCallback(
    (path: string) => {
      let newPath = path;
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (typeof value === 'string') {
          newPath = newPath.replace(`:${key}`, value);
        }
      });
      return newPath;
    },
    [params]
  );

  const tabs = useMemo(
    () =>
      entries
        ?.filter((entry) => !entry.hide)
        .map((entry) => ({
          label: entry.label === 'Edit' ? 'Account Details' : entry.label,
          value: replaceParams(entry.path),
          removeWildcard: entry.removeWildcard,
        })) ?? [],
    [entries, replaceParams]
  );

  const activeValue = useMemo(() => {
    const exact = tabs.find((t) => t.value === location.pathname);
    if (exact) return exact.value;
    const wildcard = tabs.find((t) => !t.removeWildcard && location.pathname.startsWith(t.value));
    return wildcard?.value ?? tabs[0]?.value;
  }, [tabs, location.pathname]);

  if (!entries?.length) return null;

  return (
    <Tabs
      value={activeValue}
      onValueChange={(v) => navigate(v)}
      className={cn(
        'px-3 py-0.5 shadow-[0_1px_0_0_color-mix(in_oklch,var(--color-gray-950)_8%,transparent)] dark:shadow-[0_1px_0_0_color-mix(in_oklch,var(--color-gray-50)_8%,transparent)] sticky top-0 -mt-px bg-gray-25 dark:bg-gray-900 z-10',
        className
      )}
    >
      <TabsList variant="ghost" className="gap-0.5">
        {tabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
